import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Shield, Calendar, Lightbulb, Edit } from 'lucide-react';

interface ProcessStep {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

interface SeoProcessSectionProps {
  title?: string;
  subtitle?: string;
  steps?: ProcessStep[];
}

const SeoProcessSection = ({ 
  title = "How it works: blog creation in four steps",
  subtitle = "Follow these simple steps to create professional blog articles with AI",
  steps 
}: SeoProcessSectionProps) => {
  const reduce = useReducedMotion();

  const defaultSteps = [
    {
      icon: Shield,
      title: "Lock in your brand with Brand Vault",
      description: "Start by setting the foundation. Brand Vault auto-applies your voice, values, and audience insights to every draft—so every article sounds like you, not a robot."
    },
    {
      icon: Calendar,
      title: "Plan ahead with the Blog Calendar Agent",
      description: "Keep your pipeline full with the Blog Calendar Agent. It suggests seasonal hooks, trending themes, and campaign-aligned topics to map out your monthly blog schedule."
    },
    {
      icon: Lightbulb,
      title: "Spark ideas instantly with Magic Prompt",
      description: "Feed in a keyword, theme, or even a rough thought. Magic Prompt expands it into outlines, titles, and content angles in seconds—ready to be built into full articles."
    },
    {
      icon: Edit,
      title: "Draft and refine in Studio",
      description: "Bring your blog to life in Studio: structured drafts, clear subheads, and polished copy. Edit, expand, or regenerate sections seamlessly in one chat-style workspace."
    }
  ];

  const processSteps = steps || defaultSteps;

  return (
    <section className="py-16 sm:py-20 md:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
            {title}
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Mobile: Vertical layout with connecting lines */}
        <div className="grid gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-4">
          {processSteps.map((step, index) => (
            <motion.div
              key={index}
              initial={reduce ? undefined : { opacity: 0, y: 20 }}
              whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="relative"
            >
              {/* Vertical connecting line for mobile */}
              {index < processSteps.length - 1 && (
                <div className="md:hidden absolute left-6 top-16 w-0.5 h-8 bg-gradient-to-b from-blue-500/50 to-transparent z-0" />
              )}

              {/* Card */}
              <div className="bg-card border rounded-xl p-6 h-full hover:shadow-lg active:shadow-md transition-all duration-300 relative overflow-hidden touch-manipulation">
                {/* Background icon in top-right - Smaller on mobile */}
                <div className="absolute top-3 sm:top-4 right-3 sm:right-4 opacity-10">
                  <step.icon className="h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20" />
                </div>
                
                {/* Prominent step number at top - Touch-friendly */}
                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 relative z-10 shadow-lg ${
                  index % 2 === 0 ? 'bg-cyan-500' : 'bg-orange-500'
                }`}>
                  {index + 1}
                </div>
                
                <div className="relative z-10">
                  <h3 className="text-lg sm:text-xl font-semibold mb-3 pr-16 sm:pr-20">
                    {step.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>

              {/* Horizontal connecting line for desktop */}
              {index < processSteps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-blue-500/50 to-transparent" />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SeoProcessSection;